<template>
  <v-data-table
    :headers="headers"
    :items="rows"
    dense
    class="elevation-0"
    :items-per-page="-1"
    hide-default-footer
    fixed-header
    height="calc(100vh - 145px)"
  >
    <template v-slot:header.on_hand="{ header }">
      <column-help :label="header.text" v-bind="help.on_hand" />
    </template>
    <template v-slot:header.open="{ header }">
      <column-help :label="header.text" v-bind="help.open" />
    </template>
    <template v-slot:header.delivered="{ header }">
      <column-help :label="header.text" v-bind="help.delivered" />
    </template>
    <template v-slot:header.destroyed="{ header }">
      <column-help :label="header.text" v-bind="help.destroyed" />
    </template>
    <template v-slot:header.saldo="{ header }">
      <column-help :label="header.text" v-bind="help.saldo" />
    </template>
    <template v-slot:header.available="{ header }">
      <column-help :label="header.text" v-bind="help.available" />
    </template>
    <template v-slot:item.available="{ item }">
      <v-chip class="success" x-small>
        {{ Math.max(0, item.available) }}
      </v-chip>
    </template>
    <template v-slot:item.actions="{ item }">
      <div class="d-flex align-center action-wrap">
        <v-btn x-small outlined class="mr-1" @click="adjust(item, -10)">-10</v-btn>
        <v-btn x-small outlined class="mr-2" @click="adjust(item, -1)">-1</v-btn>

        <v-btn x-small outlined class="mr-1" @click="adjust(item, +1)">+1</v-btn>
        <v-btn x-small outlined @click="adjust(item, +10)">+10</v-btn>
      </div>
    </template>
  </v-data-table>
</template>

<script>
import ColumnHelp from "./ColumnHelp.vue";

export default {
  name: "ShopInventory",
  components: { ColumnHelp },
  props: ["activeTab"],
  watch: {
    activeTab(newVal) {
      if(newVal === 1) {
        this.updateIventory();
      }
    }
  },
  data() {
    return {
      search: "",
      headers: [
        { text: "SKU", value: "sku" },
        { text: "Produkt", value: "product_name" },
        { text: "Größe", value: "size" },
        { text: "Bestand", value: "on_hand" },
        { text: "Angefragt", value: "open" },
        { text: "Ausgeliefert", value: "delivered" },
        { text: "Vernichtet", value: "destroyed" },
        { text: "Saldo", value: "saldo" },
        { text: "Im Lager", value: "available" },
        { text: "Aktionen", value: "actions", sortable: false }
      ],
      help: {
        on_hand: {
          formula: "Bestand = manuell gepflegt über +/-",
          text:
            "Gesamtmenge, die für diese Größe je beschafft wurde – auch die Stücke, die inzwischen ausgegeben oder vernichtet sind. Ändert sich nur, wenn hier per +/- korrigiert wird, nie automatisch durch eine Bestellung."
        },
        open: {
          formula: "Angefragt = Summe aller Zuteilungen im Status „Offen“",
          text:
            "Angefragt, aber noch nicht ausgegeben oder abgelehnt. Es kann mehr angefragt sein, als im Lager liegt – wie viel gedeckt ist, zeigt der Saldo."
        },
        delivered: {
          formula: "Ausgeliefert = Summe aller Zuteilungen im Status „Ausgegeben“",
          text:
            "Aktuell an Mitglieder ausgegeben. Wird ein Stück zurückgegeben, fällt es hier wieder heraus und zählt automatisch zu „Im Lager“."
        },
        destroyed: {
          formula: "Vernichtet = Summe aller Zuteilungen im Status „Vernichtet“",
          text:
            "Stücke, die nach der Ausgabe vernichtet wurden statt zurück ins Lager zu gehen. Sie bleiben Teil des Bestands, kommen aber nie wieder ins Lager."
        },
        saldo: {
          formula: "Saldo = Im Lager − Angefragt",
          text:
            "Was frei verfügbar bleibt, wenn alle offenen Anfragen bedient sind. Ein negativer Saldo heißt: es ist mehr angefragt als im Lager liegt – nachbestellen."
        },
        available: {
          formula: "Im Lager = Bestand − Ausgeliefert − Vernichtet",
          text:
            "Was aktuell physisch im Regal liegen müsste. Offene Anfragen sind hier noch nicht abgezogen – dafür ist der Saldo da. Abgelehnte Anfragen wirken sich nicht aus."
        }
      }
    };
  },
  computed: {
    globalSearch() {
      return this.$store?.state?.general?.global_search || "";
    },
    products() {
      return this.$store?.state?.shop?.products || [];
    },
    inventory(){
      return this.$store?.state?.shop?.inventory || [];
    },
    variantIndex() {
      // Map variant_id -> { size, sku, product_id, product_name }
      const map = new Map();
      for (const p of this.products) {
        const variants = p.variants || [];
        for (const v of variants) {
          map.set(v.id, {
            size: v.size || null,
            sku: v.sku || null,
            product_id: p.id,
            product_name: p.name,
          });
        }
      }
      return map;
    },
    allocationsRaw() {
      return this.$store?.state?.shop?.allocations || [];
    },
    allocationStats() {
      const stats = new Map();
      for (const user of this.allocationsRaw) {
        for (const alloc of user.allocations || []) {
          const vid = alloc.variant_id;
          const current = stats.get(vid) || {
            open: 0,
            delivered: 0,
            destroyed: 0
          };
          if (alloc.state === 1) {
            current.open += alloc.amount;
          }

          if (alloc.state === 3) {
            current.delivered += alloc.amount;
          }

          if (alloc.state === 5) {
            current.destroyed += alloc.amount;
          }

          stats.set(vid, current);
        }
      }

      return stats;
    },
    rows() {
      // Join: inventory + product/variant meta
      let list = this.inventory.map((inv) => {
        const meta = this.variantIndex.get(inv.variant_id) || {};
        const stats = this.allocationStats.get(inv.variant_id) || {
          open: 0,
          delivered: 0,
          destroyed: 0
        };
        const available = inv.on_hand - stats.destroyed - stats.delivered;
        const saldo = inv.on_hand - stats.open - stats.delivered - stats.destroyed;
        return {
          product_id: inv.product_id,
          variant_id: inv.variant_id,
          product_name: meta.product_name || `Produkt #${inv.product_id}`,
          size: meta.size || "—",
          sku: meta.sku || "—",

          destroyed: stats.destroyed,
          delivered: stats.delivered,
          open: stats.open,
          on_hand: inv.on_hand,
          available: available,
          saldo: saldo,
        };
      });
      const searchQuery = this.globalSearch.trim().toLowerCase();
      if (!searchQuery) return list;
      return list.filter(row =>
        row.product_name?.toLowerCase().includes(searchQuery) ||
        row.size?.toLowerCase().includes(searchQuery) ||
        row.sku?.toLowerCase().includes(searchQuery)
      );
    },
  },
  methods: {
    adjust(row, delta) {
      this.$store.dispatch("update", {
        context: "/shop/inventory/" + row.variant_id + "/" + (row.on_hand + delta),
        payload_type: "PUT"
      });
    },
    updateIventory(){
      this.$store.dispatch("update", { "context": "/shop/inventory"})
    }
  },
  mounted() {
    this.updateIventory();
  },
};
</script>

<style scoped>
.action-wrap {
  flex-wrap: wrap;
  gap: 4px;
}
</style>